/**
 * What Captain Bram does, decided without three or the DOM (pure; time and randomness passed in): which loop plays
 * and which one-shot, where he looks, his face, and when he blinks. bramAnimator.ts turns the answers into clips,
 * crossfades and morphs.
 */
import { GuidePriority, type GuideKind, type GuidePose, type GuideScreen } from "../guide";
import { CLIP_SECONDS, type BramArtPose, type BramClip, type BramLoop, type BramMorph } from "./bramContract";

/** The character's attitude for a step: the pose the script asked for, sharpened by the kind of step. */
export type BramMood = "welcome" | "talk" | "point" | "cheer" | "alarm" | "thumbsUp" | "laugh";

export interface BramStepInfo { readonly kind: GuideKind; readonly pose: GuidePose; readonly priority?: number; readonly screen: GuideScreen }

/** welcome card → wave; a battle safety line (boss, flyers, last stand) → alarm; otherwise the script's pose. */
export function moodFor(s: BramStepInfo): BramMood {
  if (s.kind === "welcome") return "welcome";
  if (s.priority === GuidePriority.Safety && s.screen === "level") return "alarm";
  return POSE_MOOD[s.pose];
}
const POSE_MOOD: Readonly<Record<GuidePose, BramMood>> = { neutral: "talk", pointing: "point", cheering: "cheer", thumbsUp: "thumbsUp", laughing: "laugh" };

/** The static render that stands for a mood. */
export const MOOD_ART: Readonly<Record<BramMood, BramArtPose>> = {
  welcome: "wave", talk: "neutral", point: "pointing", cheer: "cheering", alarm: "alarm", thumbsUp: "thumbsup", laugh: "cheering",
};

/** The one-shot a mood opens with (null: straight into talking). `point` holds its last pose while the step is up. */
const ENTRY_ACTION: Readonly<Record<BramMood, BramClip | null>> = {
  welcome: "wave", talk: null, point: "point", cheer: "cheer", alarm: "alarm", thumbsUp: "thumbs_up", laugh: "laugh",
};
/**
 * How an info line ends once it is typed out (tap to continue): a nod, or for good news a thumbs-up (the art's
 * suggestion: `thumbs_up` on "well done", in place of `nod`).
 */
const END_ACTION: Readonly<Record<BramMood, BramClip>> = {
  welcome: "nod", talk: "nod", point: "nod", cheer: "thumbs_up", alarm: "nod", thumbsUp: "nod", laugh: "nod",
};
const HOLDS: ReadonlySet<BramClip> = new Set(["point"]);

/** Idle fidgets on a long wait: the first after 7–14 s of nothing happening, then every 9–18 s. */
export const Fidget = { FirstMinMs: 7000, FirstMaxMs: 14000, MinMs: 9000, MaxMs: 18000, Clips: ["think", "salute", "nod"] as readonly BramClip[] } as const;
/** A step with a target: glance at it for this long first, then look at the player while talking. */
export const GLANCE_MS = 1100;

export interface BramAction { readonly clip: BramClip; readonly at: number; readonly hold: boolean; readonly ms: number }
export type BramLookMode = "target" | "player";
export type BramFace = Partial<Record<BramMorph, number>>;

export interface BramFrame {
  readonly loop: BramLoop;
  readonly action: BramAction | null;
  readonly look: BramLookMode;
  readonly face: BramFace;
}

/** Each mood's resting face (added to whatever the clips key; the mouth is the speech's). */
const MOOD_FACE: Readonly<Record<BramMood, BramFace>> = {
  welcome: { smile: 0.65, brows_up: 0.25 },
  talk: { smile: 0.3 },
  point: { smile: 0.2, brows_up: 0.15 },
  cheer: { smile: 0.85, brows_up: 0.3 },
  alarm: { brows_up: 0.55, brows_angry: 0.2, frown: 0.25 },
  thumbsUp: { smile: 0.75, brows_up: 0.2 },
  laugh: { smile: 0.9, brows_up: 0.35 },
};
/** During these one-shots the face follows the gesture. */
const ACTION_FACE: Partial<Record<BramClip, BramFace>> = {
  cheer: { smile: 1, brows_up: 0.4 }, wave: { smile: 0.8, brows_up: 0.3 }, alarm: { brows_up: 0.9, frown: 0.3 },
  think: { frown: 0.2, brows_angry: 0.15 }, salute: { smile: 0.5, brows_angry: 0.1 }, nod: { smile: 0.45 },
  laugh: { smile: 1, brows_up: 0.5 }, thumbs_up: { smile: 0.9, brows_up: 0.3 },
};

export class BramBrain {
  private mood: BramMood = "talk";
  private kind: GuideKind = "info";
  private action: BramAction | null = null;
  private hasTarget = false;
  private since = 0;
  private nudgedAt = -Infinity;
  private nextFidget = Infinity;
  private wasTalking = false;
  private nodded = false;

  constructor(private readonly rng: () => number, private readonly seconds: (c: BramClip) => number = (c) => CLIP_SECONDS[c]) {}

  get currentMood(): BramMood { return this.mood; }

  /** A step came up. */
  start(mood: BramMood, kind: GuideKind, hasTarget: boolean, now: number): void {
    this.mood = mood; this.kind = kind; this.hasTarget = hasTarget; this.since = now; this.nodded = false; this.wasTalking = false;
    const entry = ENTRY_ACTION[mood];
    this.action = entry ? this.act(entry, now) : null;
    this.nudgedAt = -Infinity;
    this.nextFidget = now + this.between(Fidget.FirstMinMs, Fidget.FirstMaxMs);
  }

  /** The target moved on or off screen. */
  setTarget(hasTarget: boolean): void { this.hasTarget = hasTarget; }

  /** The player tapped outside the spotlight: point again (or a quick nod). */
  nudge(now: number): void {
    this.action = this.act(this.mood === "point" || this.hasTarget ? "point" : "nod", now);
    this.nudgedAt = now;
  }

  update(now: number, talking: boolean): BramFrame {
    const a = this.action;
    if (a && !a.hold && now - a.at >= a.ms) this.action = null;
    // fidgets wait for a quiet spell: the clock restarts while he talks
    if (talking) this.nextFidget = Infinity;
    else if (this.wasTalking) this.nextFidget = now + this.between(Fidget.FirstMinMs, Fidget.FirstMaxMs);
    // the line is out: an info line ends on a nod (tap to continue), unless a gesture is still playing
    if (this.wasTalking && !talking && this.kind === "info" && !this.nodded && (!this.action || this.action.hold)) {
      this.nodded = true;
      if (!this.action) this.action = this.act(END_ACTION[this.mood], now);
    }
    this.wasTalking = talking;
    if (!talking && (!this.action || this.action.hold) && now >= this.nextFidget) {
      // a held point is re-made rather than dropped for a fidget: the player is still being shown where to tap
      this.action = this.action?.hold ? this.act(this.action.clip, now) : this.act(this.pick(), now);
      this.nextFidget = now + this.between(Fidget.MinMs, Fidget.MaxMs);
    }
    const glancing = now - this.since < GLANCE_MS || now - this.nudgedAt < GLANCE_MS;
    const look: BramLookMode = this.hasTarget && (this.mood === "point" || glancing) ? "target" : "player";
    return { loop: talking ? "talk" : "idle", action: this.action, look, face: this.faceFor(this.action) };
  }

  private faceFor(a: BramAction | null): BramFace {
    const base = MOOD_FACE[this.mood];
    const extra = a ? ACTION_FACE[a.clip] : undefined;
    if (!extra) return base;
    const out: BramFace = { ...base };
    for (const [k, v] of Object.entries(extra) as [BramMorph, number][]) out[k] = Math.max(out[k] ?? 0, v);
    return out;
  }

  private act(clip: BramClip, now: number): BramAction {
    return { clip, at: now, hold: HOLDS.has(clip), ms: this.seconds(clip) * 1000 };
  }
  private pick(): BramClip { return Fidget.Clips[Math.min(Fidget.Clips.length - 1, Math.floor(this.rng() * Fidget.Clips.length))]; }
  private between(lo: number, hi: number): number { return lo + (hi - lo) * this.rng(); }
}

/** A blink: lids close fast, hold a beat, open a little slower (ms). */
export const Blink = { CloseMs: 70, HoldMs: 40, OpenMs: 110, MinGapMs: 2000, MaxGapMs: 6000, DoubleChance: 0.2, DoubleGapMs: 170 } as const;
const BLINK_MS = Blink.CloseMs + Blink.HoldMs + Blink.OpenMs;

/** Lid closure `ms` into one blink: 0 open, 1 shut. */
export function blinkCurve(ms: number): number {
  if (ms < 0 || ms >= BLINK_MS) return 0;
  if (ms < Blink.CloseMs) return ms / Blink.CloseMs;
  if (ms < Blink.CloseMs + Blink.HoldMs) return 1;
  return 1 - (ms - Blink.CloseMs - Blink.HoldMs) / Blink.OpenMs;
}

/** Random blinks every 2–6 s, sometimes a double; `now()` a blink on demand (a new line, a glance). */
export class BlinkScheduler {
  private starts: number[] = [];
  private next: number;
  constructor(private readonly rng: () => number, now: number) { this.next = now + this.gap(); }

  /** Lid closure at `now` (call with non-decreasing times). */
  weight(now: number): number {
    if (now >= this.next) {
      this.starts.push(this.next);
      if (this.rng() < Blink.DoubleChance) this.starts.push(this.next + BLINK_MS + Blink.DoubleGapMs);
      this.next = this.starts[this.starts.length - 1] + BLINK_MS + this.gap();
    }
    this.starts = this.starts.filter((s) => now - s < BLINK_MS);
    let w = 0;
    for (const s of this.starts) w = Math.max(w, blinkCurve(now - s));
    return w;
  }

  /** Blink now unless one is under way. */
  trigger(now: number): void {
    if (this.starts.some((s) => now - s < BLINK_MS)) return;
    this.starts.push(now);
    this.next = now + BLINK_MS + this.gap();
  }

  private gap(): number { return Blink.MinGapMs + (Blink.MaxGapMs - Blink.MinGapMs) * this.rng(); }
}
